/* The composer: board N's hero layout (text over a control row) and board M's docked one (one row),
 * sharing the ring, the raised box, the controls and the tray. Canon values throughout; see
 * `surfaces.module.css` for the token families. */

.composer {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.hero {
  max-width: 820px;
}

.dock {
  max-width: 760px;
}

/* The builder's assistant (boards I, S): the panel's own width, a little less room under the tray. */
.panel {
  gap: var(--space-2);
  max-width: none;
}

/* The 1px ring the working light runs in. The light is a square of `--buddy-sweep` turning behind
 * the box, clipped to the ring; the box sits over it, so only the edge shows. A transform, so the
 * lap runs on the compositor and every colour stays a token. */
.ring {
  position: relative;
  padding: var(--space-1px);
  border-radius: var(--radius-xl);
}

.sweep {
  position: absolute;
  inset: var(--space-0);
  overflow: hidden;
  border-radius: var(--radius-xl);
  opacity: 0;
  transition-duration: var(--dur-slow);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.sweep::before {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150%;
  aspect-ratio: 1;
  content: '';
  background: var(--buddy-sweep);
  transform: translate(-50%, -50%);
}

.working .sweep {
  opacity: 1;
}

.working .sweep::before {
  animation-name: sweep;
  animation-duration: var(--dur-buddy-sweep);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

.box {
  position: relative;
  display: flex;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-radius: var(--radius-23px);
  box-shadow: var(--shadow-buddy-composer);
  transition-duration: var(--dur-slow);
  transition-property: transform, box-shadow, border-color;
  transition-timing-function: var(--ease);
}

.hero .box {
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-18px) var(--space-4) var(--space-14px) var(--space-20px);
}

.dock .box {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: var(--space-10px);
  align-items: end;
  padding: var(--space-14px) var(--space-3) var(--space-3) var(--space-18px);
}

/* Without a microphone the dock keeps its three columns and no empty gap. */
.dock .boxNoMic {
  grid-template-columns: auto minmax(0, 1fr) auto;
}

/* Typing lifts the box 2px (board O, „Pisanie"). */
.box:focus-within {
  border-color: var(--buddy-edge-focus);
  box-shadow: var(--shadow-buddy-composer-lifted);
  transform: translateY(-2px);
}

.working .box {
  border-color: transparent;
}

.boxOff,
.boxOff:focus-within {
  box-shadow: var(--shadow-buddy-composer-off);
  transform: none;
}

/* The plan builder's panel draws only the field and the send button, and none of the dock's named
 * cells, so the dock's four columns auto-placed the field into the narrow first one. */
.dock.panel .box {
  grid-template-columns: minmax(0, 1fr) auto;
}

.field {
  width: 100%;
  margin: var(--space-0);
  padding: var(--space-0);
  font-family: var(--font-body);
  line-height: var(--lh-lede);
  color: var(--text);
  resize: none;
  outline: none;
  background: transparent;
  border: 0;

  /* Grows with the prompt (`useAutoGrow`) up to this height, then scrolls inside the box. */
  max-height: min(320px, 45vh);
  overflow-y: auto;
}

.field::placeholder {
  color: var(--text-3);
}

.fieldHero {
  min-height: 64px;
  font-size: var(--fs-buddy-hero-field);
}

.fieldPanel {
  min-height: 44px;
  font-size: var(--fs-button-sm);
}

.fieldDock {
  flex: 1;
  min-width: 0;
  min-height: 48px;
  padding: var(--space-2px) var(--space-0);
  font-size: var(--fs-button);
}

/* The field's own box: the text area, and the status line drawn over it while it is empty. */
.fieldWrap {
  position: relative;
  display: flex;
}

.fieldWrapHero {
  width: 100%;
}

.fieldWrapDock {
  flex: 1;
  min-width: 0;
}

/* Where the placeholder would be, at the field's own type, so nothing moves when Buddy starts or
 * stops. Pointer-transparent: a click reaches the field under it. */
.status {
  position: absolute;
  inset: var(--space-0) var(--space-0) auto var(--space-0);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-lede);
  pointer-events: none;
}

.statusHero {
  font-size: var(--fs-buddy-hero-field);
}

.statusDock {
  padding: var(--space-2px) var(--space-0);
  font-size: var(--fs-button);
}

.boxOff .field::placeholder {
  color: var(--text-2);
}

.row {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.spacer {
  flex: 1;
}

.icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: var(--space-0);
  color: var(--text-2);
  cursor: default;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-radius: var(--radius-11px);
  box-shadow: var(--shadow-buddy-control);
}

.iconDim {
  opacity: 0.45;
}

.iconLive {
  cursor: pointer;
}

.iconLive:hover:not(.iconDim) {
  color: var(--text);
}

.iconGlyph {
  width: 15px;
  height: 15px;
}

.pill {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.send {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: var(--space-0);
  color: var(--on-accent-grad);
  cursor: pointer;
  background: var(--primary-fill);
  border: 0;
  border-radius: var(--radius-20px);
  box-shadow: none;
  transition-duration: var(--dur-fast);
  transition-property: transform;
  transition-timing-function: var(--ease);
}

.icon:focus-visible,
.send:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.send:hover:not([aria-disabled='true']) {
  transform: translateY(-1px);
}

.send:active:not([aria-disabled='true']) {
  transform: translateY(1px);
}

.stop {
  color: var(--text);
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge-focus);
  box-shadow: var(--shadow-buddy-control);
}

.sendOff {
  color: var(--text-3);
  cursor: default;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  box-shadow: none;
}

.sendGlyph {
  width: 17px;
  height: 17px;
}

/* The sunken strip under the box: the owner's sentence and „Wkrótce". */
.tray {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  margin: var(--space-0) var(--space-14px);
  padding: var(--space-3) var(--space-18px);
  background: var(--buddy-tray);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-top: 0;
  border-radius: var(--space-0) var(--space-0) var(--radius-lg) var(--radius-lg);
  box-shadow: var(--shadow-buddy-tray);
}

.dock .tray {
  padding: var(--space-11px) var(--space-18px);
}

/* The hero's and the dock's tray carry a dictation's status too, and never change height for it
 * (board notes, „Sizes"): 44 px under the hero, 42 px under the dock, one line either way. */
.hero .tray,
.dock:not(.panel) .tray {
  box-sizing: border-box;
  height: 44px;
}

.dock:not(.panel) .tray {
  height: 42px;
}

.trayText {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.traySoon {
  flex: none;
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--text-2);
}

/* „Wrzuć arkusz": the tray's one act (board T). */
.trayAction {
  flex: none;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: 0;
}

.trayAction:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-hair);
}

/* The panel's keys and content language (board I's `.hint`). */
.hint {
  display: flex;
  justify-content: space-between;
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* „Wyślesz, gdy Buddy skończy.": a send refused while Buddy works, said once, quietly. */
.heldBack {
  margin: var(--space-0) var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

@keyframes sweep {
  from {
    transform: translate(-50%, -50%) rotate(0);
  }

  to {
    transform: translate(-50%, -50%) rotate(1turn);
  }
}

/* ══ Dictation (`docs/design/ai-buddy-dictation-2026-10-13/`) ════════════════════════════════
 * The recording lane is laid over the composer's own controls, in the same row, so the box never
 * changes size: in the hero it shares the control row's grid cell with the idle controls; in the
 * dock it spans the „+", the field and the mic, which keep their room and only fade. */

/* The box keeps its 2px lift for the whole dictation: the trainer is composing. */
.lifted,
.lifted:focus-within {
  border-color: var(--buddy-edge-focus);
  box-shadow: var(--shadow-buddy-composer-lifted);
  transform: translateY(-2px);
}

/* The dock's cells: „+ · field · mic · send", the lane laid over the first three. Every child
 * names its cell, because grid auto-placement would push the others round a lane placed first. */
.cellSheet {
  grid-area: 1 / 1;
}

.cellField {
  grid-area: 1 / 2;
}

.cellMic {
  grid-area: 1 / 3;
}

.cellAction {
  grid-area: 1 / 4;
}

.boxNoMic .cellAction {
  grid-area: 1 / 3;
}

.cellLane {
  grid-area: 1 / 1 / 2 / 4;
}

/* The hero's control row: the idle controls and the lane in one cell, the action beside them. */
.rowBody {
  display: grid;
  flex: 1;
  align-items: center;
  min-width: 0;
}

.rowBody > * {
  grid-area: 1 / 1;
}

.idleLane {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

.lane {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-width: 0;
  height: 40px;
}

/* Entrances and exits of the two lanes and the dock's idle controls (board notes, „Motion"). */
.fades {
  transition-duration: var(--dur-base);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease);
}

.away {
  pointer-events: none;
  opacity: 0;
  transform: translateY(3px);
  transition-duration: var(--dur-fast);
  transition-timing-function: var(--ease-exit);
}

/* The dock's own controls under the lane: hidden, not moved. */
.under {
  pointer-events: none;
  opacity: 0;
}

.timer {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  min-width: 58px;
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  white-space: nowrap;
  transition-duration: var(--dur-slow);
  transition-property: color;
  transition-timing-function: var(--ease);
}

/* The last seconds: the one element carrying a verdict, beside the tray's words and the arc. */
.timerWarn {
  color: var(--buddy-warn);
}

.timerDim {
  color: var(--text-3);
}

.dot {
  flex: none;
  width: 7px;
  height: 7px;
  background: var(--text);
  border-radius: var(--r-circle);
}

.timerWarn .dot {
  background: var(--buddy-warn);
}

.timerDim .dot {
  background: var(--text-3);
}

.waveBox {
  position: relative;
  display: block;
  flex: 1;
  min-width: 0;
  height: 32px;
  mask-image: linear-gradient(90deg, transparent 0, black 56px);
}

.wave {
  display: block;
  width: 100%;
  height: 100%;
}

/* The round action's glyphs, stacked: send or stop, the ✓ and the spinner cross-fade in place. */
.actionGlyph {
  grid-area: 1 / 1;
  width: 17px;
  height: 17px;
  transition-duration: var(--dur-slow);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease-buddy-spring);
}

.actionGlyphOut {
  opacity: 0;
  transform: rotate(-70deg) scale(0.5);
}

.spin {
  color: var(--text-2);
  animation-name: spin;
  animation-duration: var(--dur-buddy-spin);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

/* The ✓ while the browser asks and the spinner while the server works: neutral, not the accent. */
.neutral,
.neutral:hover {
  color: var(--text);
  cursor: default;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge-focus);
  box-shadow: var(--shadow-buddy-control);
  transform: none;
}

.actionWrap {
  position: relative;
  flex: none;
  width: 40px;
  height: 40px;
}

/* The countdown round the ✓, 5px outside it, closing over the last ten seconds. */
.arc {
  position: absolute;
  inset: var(--space-minus-5px);
  width: 50px;
  height: 50px;
  pointer-events: none;
  opacity: 0;
  transform: rotate(-90deg);
  transition-duration: var(--dur-slow);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.arcOn {
  opacity: 1;
}

.arc circle {
  fill: none;
  stroke: var(--text-2);
  stroke-width: 1.25;
  stroke-linecap: round;
}

/* The mic and its answer: the glyph turns into a ✓ for a moment after the text lands. */
.micGlyph {
  grid-area: 1 / 1;
  width: 15px;
  height: 15px;
  transition-duration: var(--dur-base);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease-buddy-spring);
}

.micGlyphOut {
  opacity: 0;
  transform: scale(0.6);
}

.icon:active:not([aria-disabled='true'], .iconDim) {
  transform: translateY(1px);
}

/* The mic's word on hover and focus, and why it refuses when it does. */
.tipPositioner {
  z-index: 40;
}

.tip {
  padding: var(--space-7px) var(--space-10px);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text);
  white-space: nowrap;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge-focus);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-buddy-card);
  transition-duration: var(--dur-base);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease);
}

.tip[data-starting-style],
.tip[data-ending-style] {
  opacity: 0;
  transform: translateY(3px);
}

.tipKeys {
  display: inline-block;
  font-family: var(--font-body);
  margin-left: var(--space-2);
  padding: var(--space-1px) var(--space-5px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-5px);
}

.tipSub {
  display: block;
  margin-top: var(--space-2px);
  color: var(--text-2);
}

/* The field while a dictation stands over it: what the trainer wrote, read-only. */
.fieldListening::placeholder {
  color: var(--text-2);
}

/* The field's text veiled while the mirror draws the dictated words entering; the caret stays. */
.fieldVeiled {
  color: transparent;
  caret-color: var(--text);
}

.mirror {
  position: absolute;
  inset: var(--space-0);
  display: block;
  overflow: hidden;
  white-space: pre-wrap;
  pointer-events: none;
  overflow-wrap: break-word;
}

.mirrorText {
  display: block;
}

.mirrorHidden {
  color: transparent;
}

.marker {
  display: inline-block;
  width: 2px;
  height: 1.15em;
  margin: var(--space-0) var(--space-2px);
  vertical-align: -0.2em;
  background: var(--text-3);
  border-radius: var(--radius-1px);
  animation-name: marker;
  animation-duration: var(--dur-buddy-shimmer);
  animation-timing-function: var(--ease-in-out);
  animation-iteration-count: infinite;
}

.ghost {
  font-weight: var(--fw-semibold);
}

/* The tray's line: the sheet offer, or the dictation's one line of status, in the same box. */
.trayLine {
  display: flex;
  flex: 1;
  gap: var(--space-10px);
  align-items: center;
  min-width: 0;
}

.trayGlyph {
  flex: none;
  width: 14px;
  height: 14px;
  color: var(--text-2);
}

.trayGlyphDanger {
  color: var(--danger-ink);
}

/* A dictation's sentence stays on its one line; where the width runs out it is cut, never wrapped. */
.trayOneLine {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trayStrong {
  color: var(--text);
}

.trayText kbd {
  font-family: inherit;
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

@media (prefers-reduced-motion: reduce) {
  .marker,
  .spin {
    animation-name: none;
  }
}

@keyframes spin {
  to {
    transform: rotate(1turn);
  }
}

@keyframes marker {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}

/* A dictation's new line in the tray: in over 120 ms (board notes, „Motion"). */
.trayLineEnters {
  animation-name: tray-line-in;
  animation-duration: var(--dur-fast);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

@keyframes tray-line-in {
  from {
    opacity: 0;
  }
}
